import apiClient from '@/shared/services/apiClient';

/**
 * Admin-side fulfillment ops: list pending kits, mark them shipped /
 * lab-add-on complete, manually trigger the recurring 90-day scan.
 *
 * Recurring is always home-kit per spec — this service only exposes
 * the cycle-scan trigger; the dataAccess layer enforces the
 * home_kit-only rule.
 */

export type FulfillmentMethod = 'home_kit' | 'add_to_existing_draw';
export type FulfillmentStatus =
    | 'pending'
    | 'shipped'
    | 'delivered'
    | 'cancelled'
    | 'lab_addon_pending'
    | 'lab_addon_complete';

export interface AdminFulfillmentRow {
    enrollment_id: number;
    user_id: number;
    organization_id: number | null;
    organization_name: string | null;
    patient_first_name: string | null;
    patient_last_name: string | null;
    address_1: string | null;
    address_2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    recommendation_id: number;
    recommendation_name: string;
    kit_sku: string | null;
    fulfillment_method: FulfillmentMethod;
    fulfillment_status: FulfillmentStatus;
    billing_type: string;
    patient_amount_cents: number;
    employer_amount_cents: number;
    enrolled_at: string;
    next_test_due: string | null;
}

export interface MarkShippedBody {
    action?: 'ship' | 'lab_addon_complete';
    tracking_number?: string;
    tracking_carrier?: string;
    notes?: string;
}

export const AdminBioMatchFulfillmentService = {
    async list(orgId?: number): Promise<AdminFulfillmentRow[]> {
        const qs = orgId ? `?orgId=${orgId}` : '';
        return apiClient.get<AdminFulfillmentRow[]>(`/api/admin/biomatch/fulfillment${qs}`);
    },

    async mark(enrollmentId: number, body: MarkShippedBody): Promise<void> {
        await apiClient.post<void>(`/api/admin/biomatch/fulfillment/${enrollmentId}/mark`, body);
    },

    async runRecurringScan(): Promise<{requeued: number}> {
        return apiClient.post<{requeued: number}>('/api/admin/biomatch/fulfillment/run-cron', {});
    },

    async createBloodSample(input: {
        user_id: number;
        sample_type: 'whole_blood' | 'finger_prick' | 'dried_blood_spot' | 'other';
        collected_at: string;
        viability_hours?: number;
        organization_id?: number | null;
        notes?: string;
    }): Promise<unknown> {
        return apiClient.post<unknown>('/api/admin/biomatch/blood-samples', input);
    },
};

export default AdminBioMatchFulfillmentService;
